import React, { useCallback, useEffect, useRef, useState } from "react";
import { CrossIcon, MinusIcon } from "../../assets/icons";
import "./ImageView.scss";

const MIN_SCALE = 0.2;
const MAX_SCALE = 8;

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

const ImageView = ({ image, show, handleClose }) => {
  const viewportRef = useRef(null);

  const [panelPos, setPanelPos] = useState({ x: 0, y: 0 });
  const [panelSize, setPanelSize] = useState({ w: 720, h: 520 });
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);

  const [isDraggingPanel, setIsDraggingPanel] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isPanning, setIsPanning] = useState(false);

  const dragPanelRef = useRef(null);
  const resizeRef = useRef(null);
  const panRef = useRef(null);

  const centerPanel = useCallback(() => {
    if (typeof window === "undefined") return;
    const w = Math.min(720, Math.floor(window.innerWidth * 0.88));
    const h = Math.min(520, Math.floor(window.innerHeight * 0.78));
    setPanelSize({ w, h });
    setPanelPos({
      x: Math.max(8, (window.innerWidth - w) / 2),
      y: Math.max(8, (window.innerHeight - h) / 2),
    });
  }, []);

  useEffect(() => {
    if (show && image) {
      setTranslate({ x: 0, y: 0 });
      setScale(1);
      centerPanel();
    }
  }, [show, image?.url, image?.imageId, centerPanel]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, handleClose]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || !show) return;
    const onWheel = (e) => {
      e.preventDefault();
      const delta = -e.deltaY;
      const factor = Math.exp(delta * 0.0012);
      setScale((s) => clamp(s * factor, MIN_SCALE, MAX_SCALE));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [show, image?.url]);

  useEffect(() => {
    if (!isDraggingPanel && !isResizing && !isPanning) return;

    const onMove = (e) => {
      if (isDraggingPanel && dragPanelRef.current) {
        const { startClientX, startClientY, startX, startY } = dragPanelRef.current;
        const dx = e.clientX - startClientX;
        const dy = e.clientY - startClientY;
        setPanelPos({
          x: clamp(startX + dx, 0, window.innerWidth - 120),
          y: clamp(startY + dy, 0, window.innerHeight - 80),
        });
      }
      if (isResizing && resizeRef.current) {
        const { startW, startH, startClientX, startClientY } = resizeRef.current;
        const dw = e.clientX - startClientX;
        const dh = e.clientY - startClientY;
        setPanelSize({
          w: clamp(startW + dw, 280, window.innerWidth - 16),
          h: clamp(startH + dh, 220, window.innerHeight - 16),
        });
      }
      if (isPanning && panRef.current) {
        const dx = e.clientX - panRef.current.lastX;
        const dy = e.clientY - panRef.current.lastY;
        panRef.current.lastX = e.clientX;
        panRef.current.lastY = e.clientY;
        setTranslate((t) => ({ x: t.x + dx, y: t.y + dy }));
      }
    };

    const onUp = () => {
      setIsDraggingPanel(false);
      setIsResizing(false);
      setIsPanning(false);
      dragPanelRef.current = null;
      resizeRef.current = null;
      panRef.current = null;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [isDraggingPanel, isResizing, isPanning]);

  const onHeaderPointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    dragPanelRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: panelPos.x,
      startY: panelPos.y,
    };
    setIsDraggingPanel(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onResizePointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      startW: panelSize.w,
      startH: panelSize.h,
      startClientX: e.clientX,
      startClientY: e.clientY,
    };
    setIsResizing(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onViewportPointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    panRef.current = { lastX: e.clientX, lastY: e.clientY };
    setIsPanning(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const resetView = () => {
    setTranslate({ x: 0, y: 0 });
    setScale(1);
  };

  if (!show || !image) return null;

  const transformStyle = {
    transform: `translate(calc(-50% + ${translate.x}px), calc(-50% + ${translate.y}px)) scale(${scale})`,
  };

  return (
    <div className="image-view-overlay" onClick={handleClose} role="presentation">
      <div
        className="image-view-panel"
        style={{
          left: panelPos.x,
          top: panelPos.y,
          width: panelSize.w,
          height: panelSize.h,
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Image view"
      >
        <header
          className="image-view-header"
          onPointerDown={onHeaderPointerDown}
        >
          <h1 className="image-view-title">Image View</h1>
          <div
            className="image-view-header-actions"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="image-view-icon-btn"
              aria-label="Minimize close"
              onClick={handleClose}
            >
              <MinusIcon />
            </button>
            <button
              type="button"
              className="image-view-icon-btn"
              aria-label="Close"
              onClick={handleClose}
            >
              <CrossIcon />
            </button>
          </div>
        </header>

        <div className="image-view-toolbar">
          <div className="image-view-tool-group">
            <button
              type="button"
              className="image-view-tool-btn"
              onClick={() =>
                setScale((s) => clamp(s / 1.2, MIN_SCALE, MAX_SCALE))
              }
            >
              −
            </button>
            <button
              type="button"
              className="image-view-tool-btn"
              onClick={() =>
                setScale((s) => clamp(s * 1.2, MIN_SCALE, MAX_SCALE))
              }
            >
              +
            </button>
            <button
              type="button"
              className="image-view-tool-btn"
              onClick={resetView}
            >
              Reset
            </button>
          </div>
        </div>

        <div
          ref={viewportRef}
          className={`image-view-viewport${isPanning ? " is-panning" : ""}`}
          onPointerDown={onViewportPointerDown}
          onDoubleClick={resetView}
          role="presentation"
        >
          <div
            className="image-view-transform-wrap"
            style={transformStyle}
            role="presentation"
          >
            <img
              src={image.url}
              className="image-view-img"
              alt={image.imageId ? `Image ${image.imageId}` : "Enlarged image"}
              draggable={false}
            />
          </div>
          <p className="image-view-hint">
            Rotellina: zoom · Trascina: sposta · Doppio click: reset
          </p>
        </div>

        <div
          className="image-view-resize-se"
          onPointerDown={onResizePointerDown}
          title="Ridimensiona"
          role="presentation"
        />
      </div>
    </div>
  );
};

export default ImageView;
